import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';

import { toast } from 'react-toastify';

import { useDisableBodyScroll } from '../../hooks/useDisableBodyScroll';
import { WATERMARK_TYPE, serviceSettingsActions } from '../../reducers/serviceSettings';
import { UserCircleIcon, UserSettingsIcon } from '../SVGIcons';

import Button from '../common/Button';
import ConfirmModal from '../common/ConfirmModal';
import ProfileTab from './ProfileTab';
import ServiceSettingsTab from './ServiceSettingsTab';
import { ProfileData } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapStateToProps(state: any) {
  return {
    currentUser: state.currentUser,
    serviceSettings: state.serviceSettings
  };
}

/**
 * マイページ設定モーダル
 * ユーザーのプロファイル情報とサービス設定を管理
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MyPageModal = (props: any) => {
  const { currentUser, serviceSettings, onClose, initialTab = 'profile', onSettingsSaved } = props;
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState<'profile' | 'service'>(initialTab);
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const [hasSettingsChanged, setHasSettingsChanged] = useState<boolean>(false);
  const [showConfirmClose, setShowConfirmClose] = useState<boolean>(false);

  // プロファイルデータの動的生成
  const generateProfileData = (): ProfileData[] => {
    const { userName, creditConsumed = 0, creditLimit = 0, isLimitInfinity = false } = currentUser || {};
    const account = currentUser?.account;
    const planName = account?.plan_type?.name || '未設定';
    const remainingCredits = Math.max(0, creditLimit - creditConsumed);

    return [
      { label: 'アカウントID', value: userName || '未設定' },
      { label: 'ご利用プラン', value: planName },
      {
        label: '実行回数',
        value: (
          <span>
            {isLimitInfinity
              ? <>実行回数 <b>{creditConsumed}回/上限なし(アカウントのクレジット上限)</b></>
              : <>残り <b>{remainingCredits}回</b> / {creditLimit}回</>
            }
          </span>
        )
      },
    ];
  };

  const profileData = generateProfileData();

  // サービス設定の保存処理
  const handleSaveSettings = async (): Promise<void> => {
    if (!hasSettingsChanged) return;

    if (serviceSettings.watermark_type === WATERMARK_TYPE.CORPORATE_LOGO && !serviceSettings.corporate_logo_file_data) {
      dispatch(serviceSettingsActions.setError('企業ロゴ画像を登録してください'));
      return;
    }
    if (serviceSettings.watermark_type === WATERMARK_TYPE.INDIVIDUAL_INPUT && !serviceSettings.individual_input_text?.trim()) {
      dispatch(serviceSettingsActions.setError('個別入力のテキストを入力してください'));
      return;
    }

    if (serviceSettings.annotation_is_visible && !serviceSettings.annotation_text?.trim()) {
      dispatch(serviceSettingsActions.setError('注釈テキストを入力してください'));
      return;
    }

    await dispatch(serviceSettingsActions.saveServiceSettings());
    setHasSettingsChanged(false);

    if (onSettingsSaved) {
      onSettingsSaved();
    }
  };

  // コンポーネントマウント時にサービス設定を取得
  useEffect(() => {
    dispatch(serviceSettingsActions.fetchServiceSettings());
  }, [dispatch]);

  useDisableBodyScroll();

  // メッセージをreact-toastifyで表示
  const toastOptions = { position: 'top-right' as const, style: { marginTop: 60 } };

  useEffect(() => {
    if (serviceSettings.success_message) {
      toast.success(serviceSettings.success_message, toastOptions);
      dispatch(serviceSettingsActions.clearMessages());
    }
  }, [serviceSettings.success_message, dispatch]);

  useEffect(() => {
    if (serviceSettings.error) {
      toast.error(serviceSettings.error, toastOptions);
      dispatch(serviceSettingsActions.clearMessages());
    }
  }, [serviceSettings.error, dispatch]);

  const handleClose = () => {
    if (hasSettingsChanged) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.18)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 18,
        width: '80vw',
        height: '85vh',
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
        display: 'flex',
        position: 'relative',
        padding: 0,
        overflow: 'hidden'
      }}>
        {/* サイドメニュー */}
        <aside style={{
          width: '25%',
          background: '#f7f7f7',
          padding: '32px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8
        }}>
          <h2 style={{ fontWeight: 600, fontSize: 20, color: '#222', textAlign: 'center' }}>アカウント設定</h2>
          {[
            { key: 'profile' as const, label: 'プロファイル', icon: <UserCircleIcon /> },
            { key: 'service' as const, label: 'サービス設定', icon: <UserSettingsIcon /> },
          ].map(item => (
            <button
              type="button"
              key={item.key}
              onClick={() => setActiveTab(item.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 16,
                fontWeight: 500,
                background: activeTab === item.key ? '#fff' : 'transparent',
                color: activeTab === item.key ? '#222' : '#aaa',
                border: 'none',
                outline: 'none',
                cursor: 'pointer',
                padding: '10px 12px',
                borderRadius: 8,
                margin: '0',
                boxShadow: activeTab === item.key ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24 }}>
                {item.icon}
              </span>
              {item.label}
            </button>
          ))}
        </aside>

        {/* メインコンテンツエリア */}
        <section style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* ヘッダー部分 */}
          <div style={{ padding: '32px 24px 8px 24px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ fontSize: 20, fontWeight: 600, color: '#222' }}>
                {activeTab === 'profile' ? 'プロファイル' : 'サービス設定'}
              </h3>
              <button type="button" onClick={handleClose} style={{
                background: 'none',
                border: 'none',
                fontSize: 18,
                cursor: 'pointer',
                color: '#666'
              }}>
                閉じる
              </button>
            </div>
            <hr style={{ border: 'none', height: 1, background: '#bbb', margin: 0, marginTop: 12 }} />
          </div>

          {/* コンテンツ部分（スクロール可能） */}
          <div style={{ flex: 1, padding: '0 24px 32px 24px', overflowY: 'auto' }}>
            {activeTab === 'profile' && <ProfileTab profileData={profileData} />}
            {activeTab === 'service' && (
              <ServiceSettingsTab
                serviceSettings={serviceSettings}
                selectedFileName={selectedFileName}
                setSelectedFileName={setSelectedFileName}
                onSettingsChanged={() => setHasSettingsChanged(true)}
              />
            )}
          </div>

          {/* フッター部分（サービス設定タブのみ） */}
          {activeTab === 'service' && (
            <div style={{
              borderTop: '1px solid #eee',
              padding: '16px 24px',
              display: 'flex',
              justifyContent: 'flex-end',
              background: '#fafafa'
            }}>
              <Button
                onClick={handleSaveSettings}
                disabled={serviceSettings.isLoading || !hasSettingsChanged}
              >
                {serviceSettings.isLoading ? '保存中...' : '保存'}
              </Button>
            </div>
          )}
        </section>
      </div>

      <ConfirmModal
        show={showConfirmClose}
        onClose={() => setShowConfirmClose(false)}
        onConfirm={handleConfirmClose}
        title="サービス設定の編集内容を破棄しますか？"
        confirmText="破棄"
        cancelText="キャンセル"
        zIndex={2000}
      />
    </div>
  );
};

export default connect(mapStateToProps)(MyPageModal);
